import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, brl, formatDate, maskCPF } from "../lib/api";
import { maskCep, lookupCep } from "../lib/cep";
import { maskCpf, isValidCpf, CPF_INVALID_MSG } from "../lib/cpf";
import { maskPhone, isValidPhone, PHONE_INVALID_MSG } from "../lib/phone";
import { ScoreRing, StatusBadge } from "../components/Score";
import { ClientTierBadge } from "../components/TierBadge";
import RiskMeter from "../components/RiskMeter";
import CreditCard from "../components/CreditCard";
import BRLInput from "../components/BRLInput";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  Brain, ChatCircle, MapPin, WhatsappLogo, FilePdf, Image as ImageIcon,
  UserCircle, FloppyDisk, ShoppingBag, Receipt, CurrencyCircleDollar,
  WarningCircle, CheckCircle, Storefront, PaperPlaneTilt, HandCoins,
} from "@phosphor-icons/react";

function Field({ label, value, onChange, type = "text", placeholder = "", testid, readOnly = false }) {
  return (
    <div>
      <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">{label}</label>
      <input
        type={type} className="input-underline" value={value || ""} placeholder={placeholder}
        readOnly={readOnly} disabled={readOnly}
        onChange={(e) => onChange && onChange(e.target.value)} data-testid={testid}
      />
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="border border-[#EBE5DF] bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#5C5A57]">
        <Icon size={14} weight="duotone" color={accent} /> {label}
      </div>
      <div className="font-display text-2xl font-bold mt-1 tabular" style={accent ? { color: accent } : {}}>{value}</div>
      {sub && <div className="text-[11px] text-[#5C5A57] mt-1">{sub}</div>}
    </div>
  );
}

const COMP_LABELS = {
  dados_pessoais: "Dados pessoais",
  documento: "Documento (CPF)",
  telefone: "Telefone",
  endereco: "Endereço",
};
const COMP_STYLES = {
  complete: { dot: "🟢", label: "Completo", color: "#3F6B45", bg: "#EFF5EF", border: "#3F6B45" },
  incomplete: { dot: "🟡", label: "Incompleto", color: "#8A6D1F", bg: "#FBF6E7", border: "#C9A227" },
  blocked: { dot: "🔴", label: "Bloqueado", color: "#8B5E5E", bg: "#FBEDED", border: "#B4564F" },
};

function CompletenessCard({ comp }) {
  if (!comp) return null;
  const s = COMP_STYLES[comp.status] || COMP_STYLES.incomplete;
  return (
    <section className="border bg-white p-5 mb-6" style={{ borderColor: s.border }} data-testid="client-completeness-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <h2 className="section-title">Completude do cadastro</h2>
          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: s.bg, color: s.color }} data-testid="completeness-status">
            {s.dot} {s.label}
          </span>
        </div>
        <div className="font-display text-2xl font-bold tabular" style={{ color: s.color }} data-testid="completeness-percent">{comp.percent}%</div>
      </div>
      <div className="h-2 w-full bg-[#F0EBE5] rounded-full overflow-hidden mb-4">
        <div className="h-full rounded-full transition-all" style={{ width: `${comp.percent}%`, background: s.color }} data-testid="completeness-bar" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        {Object.keys(COMP_LABELS).map((k) => (
          <div key={k} className="flex items-center gap-1.5 text-xs" data-testid={`completeness-item-${k}`}>
            <span>{comp.checklist?.[k] ? "✅" : "⬜"}</span>
            <span className={comp.checklist?.[k] ? "text-[#0A0A0A]" : "text-[#9A938C]"}>{COMP_LABELS[k]}</span>
          </div>
        ))}
      </div>
      {!comp.is_operational && (
        <div className="text-xs leading-relaxed p-3 rounded" style={{ background: s.bg, color: s.color }} data-testid="completeness-blocked-warning">
          <strong>Operações bloqueadas.</strong> Complete o cadastro (100%) para liberar novas vendas,
          promissória digital, limite de crédito, análise de score e ativação.
          {comp.missing?.length > 0 && <> Pendências: <strong>{comp.missing.join(", ")}</strong>.</>}
        </div>
      )}
    </section>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [client, setClient] = useState(null);
  const [reseller, setReseller] = useState(null);
  const [stats, setStats] = useState({});
  const [sales, setSales] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [pixDirect, setPixDirect] = useState({});
  const [charging, setCharging] = useState(null);
  const [waConvo, setWaConvo] = useState(null);
  const [risk, setRisk] = useState(null);
  const [insights, setInsights] = useState(null);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showLimit, setShowLimit] = useState(false);
  const [reqIncrement, setReqIncrement] = useState(0);
  const [reason, setReason] = useState("");
  const [tab, setTab] = useState("sales");
  const [cepLoading, setCepLoading] = useState(false);
  const [cepFound, setCepFound] = useState(false);

  // Agrega saldo/pago por venda a partir das parcelas enriquecidas (open_amount/paid_total/status)
  const saleAgg = React.useMemo(() => {
    const m = {};
    (installments || []).forEach((i) => {
      const sid = i.sale_id;
      if (!sid) return;
      const a = m[sid] || (m[sid] = { saldo: 0, paid: 0, hasLate: false, hasPartial: false, allPaid: true });
      a.saldo += Number(i.open_amount ?? (i.status === "paid" ? 0 : i.amount) ?? 0);
      a.paid += Number(i.paid_total || 0);
      if (i.status !== "paid") a.allPaid = false;
      if (i.status === "partial_late") a.hasLate = true;
      if (i.status === "partial" || i.status === "partial_late") a.hasPartial = true;
    });
    Object.values(m).forEach((a) => { a.saldo = Math.round(a.saldo * 100) / 100; a.paid = Math.round(a.paid * 100) / 100; });
    return m;
  }, [installments]);

  const load = async () => {
    const { data } = await api.get(`/clients/${id}/profile`);
    setClient(data.client);
    setReseller(data.reseller);
    setStats(data.stats || {});
    setSales(data.sales || []);
    setInstallments(data.installments || []);
    setReceipts(data.receipts || []);
    setPixDirect(data.pix_direct || {});
    setForm({
      name: data.client.name || "", cpf: maskCpf(data.client.cpf || ""),
      phone: maskPhone(data.client.phone || ""), email: data.client.email || "",
      birth_date: data.client.birth_date || "",
      notes: data.client.notes || "",
      address: { cep: "", street: "", number: "", complement: "", district: "", city: "", state: "", ...(data.client.address || {}) },
    });
    try { setRisk((await api.get(`/intelligence/risk/${id}`)).data); } catch { setRisk(null); }
    try { setWaConvo((await api.get(`/whatsapp/conversations/${id}`)).data); } catch { setWaConvo(null); }
  };

  useEffect(() => { (async () => { await load(); })(); }, [id]);

  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  const onCep = async (v) => {
    const masked = maskCep(v);
    setAddr("cep", masked);
    setCepFound(false);
    if (masked.replace(/\D/g, "").length === 8) {
      setCepLoading(true);
      try {
        const res = await lookupCep(masked);
        if (res.ok) { setForm((f) => ({ ...f, address: { ...f.address, ...res.data } })); setCepFound(true); }
      } catch { /* manual */ } finally { setCepLoading(false); }
    }
  };

  const save = async () => {
    if (!isValidCpf(form.cpf)) { toast.error(CPF_INVALID_MSG); return; }
    if (!isValidPhone(form.phone)) { toast.error(PHONE_INVALID_MSG); return; }
    setSaving(true);
    try {
      await api.patch(`/clients/${id}`, {
        name: form.name, cpf: form.cpf, phone: form.phone, email: form.email || null,
        birth_date: form.birth_date, notes: form.notes, address: form.address,
      });
      toast.success("Dados do cliente salvos");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const runAi = async () => {
    try { setInsights((await api.post(`/ai/analyze-client/${id}`)).data); } catch { toast.error("Erro IA"); }
  };

  const sendReminder = async () => {
    if (!client) return;
    try {
      await api.post("/whatsapp/send", {
        to: client.phone, client_id: client.id, template: "reminder",
        message: `Oi ${client.name.split(" ")[0]} <3 passando para lembrar das suas parcelas! Equipe Cris Lingerie.`,
      });
      toast.success("Mensagem WhatsApp registrada (MOCK)");
    } catch { toast.error("Erro ao registrar"); }
  };

  const submitLimit = async (e) => {
    e.preventDefault();
    if (!reqIncrement || reqIncrement <= 0) return toast.error("Informe um valor maior que zero");
    try {
      await api.post(`/clients/${id}/limit-request`, { requested_limit: Number(reqIncrement), reason });
      toast.success(`Solicitacao enviada: +${brl(reqIncrement)}`);
      setShowLimit(false); setReqIncrement(0); setReason("");
    } catch { toast.error("Erro"); }
  };

  const updateStatus = async (newStatus) => {
    try { await api.patch(`/clients/${id}`, { status: newStatus }); toast.success("Status atualizado"); await load(); }
    catch { toast.error("Erro"); }
  };

  const cobrar = async (instId, forceRetry = false) => {
    setCharging(instId);
    try {
      await api.post(`/whatsapp/charge/${instId}${forceRetry ? "?force=true" : ""}`);
      toast.success("Cobranca enviada pelo WhatsApp oficial");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || "Erro ao enviar cobranca";
      if (e.response?.status === 409) {
        if (window.confirm(`${detail}\n\nReenviar mesmo assim?`)) { setCharging(null); return cobrar(instId, true); }
      } else { toast.error(detail); }
    } finally { setCharging(null); }
  };

  if (!client || !form) return <div className="text-sm tracking-widest uppercase text-[#5C5A57]" data-testid="client-detail-loading">Carregando...</div>;

  const today = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; })();
  const clientBase = isAdmin ? "/admin/clientes" : "/revenda/clientes";

  const tabBtn = (tid, label, Icon, count) => (
    <button onClick={() => setTab(tid)} data-testid={`tab-${tid}`}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs tracking-widest uppercase font-medium border-b-2 transition-colors ${tab === tid ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#5C5A57] hover:text-[#0A0A0A]"}`}>
      <Icon size={15} weight="duotone" /> {label}{typeof count === "number" ? ` (${count})` : ""}
    </button>
  );

  return (
    <div data-testid="client-detail-page">
      {/* 1. Header - dados principais */}
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Cliente</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{client.name}</h1>
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#5C5A57] items-center">
            <span>{maskCPF(client.cpf)}</span><span>·</span><span>{client.phone}</span>
            <span>·</span><StatusBadge status={client.status} />
            <ClientTierBadge credit_limit={client.credit_limit} compact />
            {reseller && (
              <>
                <span>·</span>
                <Link to={isAdmin ? `/admin/revendedoras/${reseller.id}` : "#"} className="inline-flex items-center gap-1 hover:underline" data-testid="client-reseller-link">
                  <Storefront size={12} /> {reseller.name}
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/revenda/venda/nova?client=${id}`} className="brand" data-testid="cta-sale-from-client">+ Venda</Link>
          <button onClick={sendReminder} className="brand" style={{ background: "#5C5A57" }} data-testid="send-whatsapp-btn"><ChatCircle size={16} className="inline mr-2" />Lembrete</button>
        </div>
      </div>

      {/* Completude do cadastro (P0) */}
      <CompletenessCard comp={client.completeness} />

      {/* 2. Indicadores financeiros */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3" data-testid="client-financial-stats">
        <Stat icon={ShoppingBag} label="Total comprado" value={brl(stats.total_purchased)} sub={`${stats.sales_count || 0} venda(s)`} testid="stat-total-purchased" />
        <Stat icon={CheckCircle} label="Total pago" value={brl(stats.total_paid)} sub={`${stats.paid_installments_count || 0} parcela(s)`} accent="#3F6B45" testid="stat-total-paid" />
        <Stat icon={CurrencyCircleDollar} label="Em aberto" value={brl(stats.total_open)} sub={`${stats.open_installments_count || 0} parcela(s)`} testid="stat-total-open" />
        <Stat icon={WarningCircle} label="Parcelas atrasadas" value={stats.overdue_count || 0} sub={brl(stats.overdue_amount)} accent={(stats.overdue_count || 0) > 0 ? "#8B5E5E" : undefined} testid="stat-overdue" />
      </div>

      {/* 3. Score / Risco + Limite */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="border border-[#EBE5DF] bg-white p-6 flex flex-col items-center" data-testid="client-score-card">
          <ScoreRing score={client.score} />
          <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mt-4">Score interno</div>
        </div>

        <div className="border border-[#EBE5DF] bg-white p-6" data-testid="client-limit-card">
          <div className="kpi-label">Limite Total</div>
          <div className="kpi-value tabular">{brl(client.credit_limit)}</div>
          <div className="text-xs text-[#5C5A57] mt-3">Disponivel agora</div>
          <div className="font-display text-2xl font-bold tabular mt-1">{brl(client.available_limit)}</div>
          <div className="text-xs text-[#5C5A57] mt-1">Em uso: {brl(client.open_balance)}</div>
          <button onClick={() => setShowLimit(!showLimit)} className="text-xs tracking-widest uppercase font-medium hover:underline mt-4" data-testid="request-limit-btn">Solicitar aumento</button>
          {showLimit && (
            <form onSubmit={submitLimit} className="mt-3 space-y-2 border-t border-[#EBE5DF] pt-3">
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Quanto a mais (R$)</label>
              <BRLInput value={reqIncrement} onChange={setReqIncrement} testid="limit-increment-input" />
              {reqIncrement > 0 && (
                <div className="text-[10px] text-[#5C5A57] tabular">
                  Limite atual {brl(client.credit_limit)} + {brl(reqIncrement)} = <strong>{brl(Number(client.credit_limit) + reqIncrement)}</strong>
                </div>
              )}
              <input placeholder="Motivo (opcional)" value={reason} onChange={(e) => setReason(e.target.value)} className="input-underline" />
              <button type="submit" className="brand w-full" data-testid="submit-limit-request">Enviar solicitacao</button>
            </form>
          )}
        </div>

        <div className="border border-[#EBE5DF] bg-[#F5F0EB] p-6" data-testid="client-validation-card">
          <h3 className="section-title text-lg mb-3">Validacao</h3>
          {client.selfie_data_url ? (
            <img src={client.selfie_data_url} alt="selfie" className="w-24 h-24 object-cover border border-[#EBE5DF] mb-3" />
          ) : <div className="text-xs text-[#5C5A57]">Sem selfie cadastrada.</div>}
          {client.geolocation?.label && (
            <div className="text-xs text-[#5C5A57] flex items-center gap-1"><MapPin size={12} />{client.geolocation.label}</div>
          )}
          <div className="text-xs text-[#5C5A57] mt-2">Cadastrado em {formatDate(client.created_at)}</div>
          {isAdmin && (
            <div className="mt-4 flex flex-wrap gap-2">
              {client.status !== "active" && <button className="text-[10px] tracking-widest uppercase hover:underline" onClick={() => updateStatus("active")} data-testid="set-active-btn">Ativar</button>}
              {client.status !== "blocked" && <button className="text-[10px] tracking-widest uppercase text-[#8B5E5E] hover:underline" onClick={() => updateStatus("blocked")} data-testid="set-blocked-btn">Bloquear</button>}
              {client.status !== "observation" && <button className="text-[10px] tracking-widest uppercase hover:underline" onClick={() => updateStatus("observation")} data-testid="set-observation-btn">Observar</button>}
            </div>
          )}
        </div>
      </div>

      {risk && <div className="mb-6"><RiskMeter risk={risk} /></div>}

      <CreditCard clientId={id} onLimitChanged={load} />

      {/* PIX direto da revendedora */}
      <section className="border border-[#3F6B45] bg-white p-5 mb-6" data-testid="client-pix-direct">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="section-title flex items-center gap-2"><HandCoins size={18} weight="duotone" color="#3F6B45" /> PIX direto da revendedora</h2>
          {pixDirect.last_request && (
            <span className={`badge ${pixDirect.last_request.status === "conferido" ? "green" : pixDirect.last_request.status === "comprovante_rejeitado" ? "red" : "medio"}`} data-testid="pix-direct-last-status">{pixDirect.last_request.status}</span>
          )}
        </div>
        {(pixDirect.requests_count || 0) === 0 ? (
          <div className="text-sm text-[#5C5A57]">Nenhuma solicitacao de PIX direto registrada para esta cliente.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Ultima solicitacao</div>
              <div className="font-medium mt-1">{formatDate(pixDirect.last_request?.requested_at)}</div>
              <div className="text-[11px] text-[#5C5A57]">por {pixDirect.last_request?.provided_by === "ia" ? "IA" : pixDirect.last_request?.provided_by}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Solicitacoes</div>
              <div className="font-display text-2xl font-bold mt-1 tabular">{pixDirect.requests_count}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Pagamentos diretos</div>
              <div className="font-display text-2xl font-bold mt-1 tabular" style={{ color: "#3F6B45" }}>{pixDirect.direct_payments_count}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Ultimo comprovante</div>
              {pixDirect.last_receipt
                ? <><div className="font-medium mt-1">{formatDate(pixDirect.last_receipt.received_at)}</div><span className={`badge ${pixDirect.last_receipt.status === "approved" ? "green" : pixDirect.last_receipt.status === "rejected" ? "red" : "medio"}`}>{pixDirect.last_receipt.status}</span></>
                : <div className="text-[#5C5A57] mt-1">—</div>}
            </div>
          </div>
        )}
      </section>

      {/* Cadastro editavel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <section className="border border-[#EBE5DF] bg-white p-6">
          <h2 className="section-title flex items-center gap-2 mb-5"><UserCircle size={18} weight="duotone" /> Dados pessoais</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome completo" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testid="cd-name" />
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Telefone</label>
              <input className="input-underline" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: maskPhone(e.target.value) }))} placeholder="(11) 99999-9999" inputMode="numeric" maxLength={16} data-testid="cd-phone" />
              {form.phone.replace(/\D/g, "").length >= 10 && !isValidPhone(form.phone) && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="cd-phone-error">{PHONE_INVALID_MSG}</div>}
            </div>
            <Field label="E-mail" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} type="email" testid="cd-email" />
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">CPF</label>
              <input className="input-underline" value={form.cpf} onChange={(e) => setForm((f) => ({ ...f, cpf: maskCpf(e.target.value) }))} placeholder="000.000.000-00" inputMode="numeric" maxLength={14} data-testid="cd-cpf" />
              {form.cpf.replace(/\D/g, "").length === 11 && !isValidCpf(form.cpf) && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="cd-cpf-error">{CPF_INVALID_MSG}</div>}
            </div>
            <Field label="RG" value={client.rg} readOnly testid="cd-rg" />
            <Field label="Nascimento" value={form.birth_date} onChange={(v) => setForm((f) => ({ ...f, birth_date: v }))} type="date" testid="cd-birth" />
          </div>
        </section>

        <section className="border border-[#EBE5DF] bg-white p-6">
          <h2 className="section-title flex items-center gap-2 mb-5"><MapPin size={18} weight="duotone" /> Endereco</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">CEP {cepLoading && <span className="text-[#5C5A57]">(Buscando endereço...)</span>}</label>
              <input className="input-underline" value={form.address.cep || ""} onChange={(e) => onCep(e.target.value)} placeholder="00000-000" inputMode="numeric" maxLength={9} data-testid="cd-cep" />
              {cepFound && !cepLoading && <div className="text-[10px] text-[#3F6B45] mt-1 flex items-center gap-1" data-testid="cd-cep-success"><CheckCircle size={12} weight="fill" /> Endereço localizado com sucesso.</div>}
            </div>
            <Field label="Rua" value={form.address.street} onChange={(v) => setAddr("street", v)} testid="cd-street" />
            <Field label="Numero" value={form.address.number} onChange={(v) => setAddr("number", v)} testid="cd-number" />
            <Field label="Complemento" value={form.address.complement} onChange={(v) => setAddr("complement", v)} testid="cd-complement" />
            <Field label="Bairro" value={form.address.district} onChange={(v) => setAddr("district", v)} testid="cd-district" />
            <Field label="Cidade" value={form.address.city} onChange={(v) => setAddr("city", v)} testid="cd-city" />
            <Field label="Estado" value={form.address.state} onChange={(v) => setAddr("state", v)} testid="cd-state" />
          </div>
        </section>
      </div>

      <section className="border border-[#EBE5DF] bg-white p-6 mb-3">
        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Observacoes</label>
        <textarea rows={2} className="w-full p-3 border border-[#EBE5DF] text-sm" value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="cd-notes" />
      </section>
      <button onClick={save} disabled={saving} className="brand mb-8 inline-flex items-center gap-2" data-testid="cd-save-btn"><FloppyDisk size={16} /> {saving ? "Salvando..." : "Salvar cadastro"}</button>

      {/* 4. Historicos em abas */}
      <div className="border-b border-[#EBE5DF] flex flex-wrap gap-1 mb-4" data-testid="client-history-tabs">
        {tabBtn("sales", "Compras", ShoppingBag, sales.length)}
        {tabBtn("installments", "Parcelas", CurrencyCircleDollar, installments.length)}
        {tabBtn("receipts", "Comprovantes", Receipt, receipts.length)}
        {tabBtn("convo", "Conversas", WhatsappLogo, waConvo?.messages?.length)}
      </div>

      {tab === "sales" && (
        <div className="table-wrap mb-8" data-testid="tab-content-sales">
          <table className="crl">
            <thead><tr><th>#</th><th>Data</th><th>Produtos</th><th>Parcelas</th><th>Total</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {sales.map((s) => {
                const agg = saleAgg[s.id] || {};
                const partial = agg.paid > 0 && !agg.allPaid && agg.saldo > 0.005;
                return (
                  <tr key={s.id} data-testid={`sale-row-${s.id}`}>
                    <td>#{s.number}</td>
                    <td className="text-[#5C5A57] text-xs">{formatDate(s.created_at)}</td>
                    <td>{(s.products || []).length} item(s)</td>
                    <td>{s.installments}x</td>
                    <td className="tabular font-medium">
                      {partial ? brl(agg.saldo) : brl(s.total)}
                      {partial && <div className="text-[10px] text-[#9A8A98] tabular" data-testid={`sale-partial-${s.id}`}>pago {brl(agg.paid)} de {brl(s.total)}</div>}
                    </td>
                    <td>{partial
                      ? <span className={`badge ${agg.hasLate ? "red" : "medio"}`}>{agg.hasLate ? "Parcial em atraso" : "Parcialmente paga"}</span>
                      : <StatusBadge status={s.status} />}</td>
                    <td><Link className="text-xs uppercase tracking-widest font-medium hover:underline" to={`/vendas/${s.id}`}>Ver</Link></td>
                  </tr>
                );
              })}
              {sales.length === 0 && <tr><td colSpan="7" className="text-center text-sm text-[#5C5A57] py-8">Sem vendas registradas.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "installments" && (
        <div className="table-wrap mb-8" data-testid="tab-content-installments">
          <table className="crl">
            <thead><tr><th>#</th><th>Vencimento</th><th>Valor</th><th>Status</th><th>Pago em</th><th></th></tr></thead>
            <tbody>
              {installments.map((i) => {
                const overdue = i.status !== "paid" && (i.due_date || "") < today;
                const partial = Number(i.paid_total || 0) > 0 && i.status !== "paid";
                const badge = i.status === "paid" ? { c: "green", t: "Paga" }
                  : i.status === "partial_late" ? { c: "red", t: "Parcial em atraso" }
                  : i.status === "partial" ? { c: "medio", t: "Parcialmente paga" }
                  : overdue ? { c: "red", t: "Atrasada" } : { c: "medio", t: "Em aberto" };
                return (
                  <tr key={i.id} data-testid={`installment-row-${i.id}`}>
                    <td>{i.number}</td>
                    <td className="text-[#5C5A57] text-xs">{formatDate(i.due_date)}</td>
                    <td className="tabular font-medium">
                      {brl(i.status === "paid" ? (i.amount) : (i.open_amount ?? i.amount))}
                      {partial && <div className="text-[10px] text-[#9A8A98] tabular">pago {brl(i.paid_total)} · de {brl(i.amount)}</div>}
                    </td>
                    <td><span className={`badge ${badge.c}`}>{badge.t}</span></td>
                    <td className="text-[#5C5A57] text-xs">{i.paid_at ? formatDate(i.paid_at) : "-"}</td>
                    <td>
                      <div className="flex items-center gap-3">
                        {i.status !== "paid" && (
                          <button onClick={() => cobrar(i.id)} disabled={charging === i.id} className={`text-xs uppercase tracking-widest font-medium inline-flex items-center gap-1 ${overdue ? "text-[#8B5E5E]" : ""} hover:underline disabled:opacity-50`} data-testid={`charge-installment-${i.id}`} title="Gerar link oficial e enviar cobranca pelo WhatsApp">
                            <PaperPlaneTilt size={13} /> {charging === i.id ? "Enviando..." : "Cobrar agora"}
                          </button>
                        )}
                        {i.sale_id && <Link className="text-xs uppercase tracking-widest font-medium hover:underline" to={`/vendas/${i.sale_id}`}>Venda</Link>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {installments.length === 0 && <tr><td colSpan="6" className="text-center text-sm text-[#5C5A57] py-8">Sem parcelas.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "receipts" && (
        <div className="mb-8" data-testid="tab-content-receipts">
          {receipts.length === 0 ? (
            <div className="text-center text-sm text-[#5C5A57] py-8 border border-[#EBE5DF] bg-white">Nenhum comprovante recebido.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {receipts.map((r) => (
                <div key={r.id} className="border border-[#EBE5DF] bg-white p-4 flex gap-3" data-testid={`receipt-${r.id}`}>
                  <div className="shrink-0">
                    {r.media_type === "image" && (r.media_url || r.media_base64)
                      ? <img src={r.media_url || r.media_base64} alt="comprovante" className="w-16 h-16 object-cover border border-[#EBE5DF]" />
                      : <div className="w-16 h-16 flex items-center justify-center bg-[#F5F0EB] border border-[#EBE5DF]"><FilePdf size={24} /></div>}
                  </div>
                  <div className="text-xs">
                    <div className="font-medium">{r.probable_amount ? brl(r.probable_amount) : "Comprovante"}</div>
                    <div className="text-[#5C5A57]">{formatDate(r.received_at)}</div>
                    <span className={`badge mt-1 inline-block ${r.status === "approved" ? "green" : r.status === "rejected" ? "red" : "medio"}`}>{r.status === "approved" ? "Aprovado" : r.status === "rejected" ? "Rejeitado" : "Pendente"}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Link to={isAdmin ? "/admin/comprovantes" : "/revenda/comprovantes"} className="inline-flex items-center gap-1 mt-3 text-xs tracking-widest uppercase font-medium hover:underline" data-testid="open-receipts-queue"><Receipt size={14} /> Abrir fila de comprovantes</Link>
        </div>
      )}

      {tab === "convo" && (
        <div className="mb-8" data-testid="tab-content-convo">
          {waConvo && waConvo.messages && waConvo.messages.length > 0 ? (
            <div className="border border-[#EBE5DF] bg-white p-6" data-testid="client-wa-conversation">
              <div className="flex items-center justify-between mb-3">
                <h2 className="section-title flex items-center gap-2"><WhatsappLogo size={18} weight="duotone" color="#25D366" /> Conversa WhatsApp</h2>
                <div className="flex items-center gap-3 text-xs text-[#5C5A57]">
                  {waConvo.conversation?.last_at && <span>Ultima: {formatDate(waConvo.conversation.last_at)}</span>}
                  <Link to={isAdmin ? "/admin/crm" : "/revenda/crm"} className="uppercase tracking-widest font-medium hover:underline" data-testid="client-open-crm">Abrir CRM</Link>
                </div>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto bg-[#FAF7F3] p-3 border border-[#F0EBE5]">
                {waConvo.messages.slice(-20).map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${m.direction === "out" ? "bg-[#DCF8C6]" : "bg-white border border-[#EBE5DF]"}`}>
                      {m.type === "image" ? (m.media_url || m.media_base64 ? <img src={m.media_url || m.media_base64} alt="img" className="max-w-[180px] rounded" /> : <span className="flex items-center gap-1 text-xs"><ImageIcon size={14} /> Imagem</span>)
                        : m.type === "document" ? <span className="flex items-center gap-1 text-xs"><FilePdf size={14} /> {m.media_filename || "Documento"}</span>
                        : <span className="whitespace-pre-wrap break-words">{m.text || `[${m.type}]`}</span>}
                      <div className="text-[9px] text-[#9A938C] mt-1 text-right">{formatDate(m.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-sm text-[#5C5A57] py-8 border border-[#EBE5DF] bg-white">Nenhuma conversa registrada.</div>
          )}
        </div>
      )}

      {/* 5. Analise IA */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="section-title">Inteligencia</h2>
        <button onClick={runAi} className="text-xs tracking-widest uppercase font-medium hover:underline flex items-center gap-1" data-testid="analyze-ai-btn"><Brain size={14} /> Analisar com IA</button>
      </div>
      {insights && (
        <div className="border border-[#0A0A0A] bg-[#0A0A0A] text-white p-6 mb-6" data-testid="ai-insights">
          <div className="text-[10px] tracking-widest uppercase mb-2">Analise IA</div>
          {insights.ai_text ? (
            <div className="text-sm leading-relaxed whitespace-pre-wrap">{insights.ai_text}</div>
          ) : (
            <div className="text-sm">Score {insights.summary.score} ({insights.summary.classification}) · {insights.summary.sales_count} vendas · {insights.summary.overdue_installments} parcelas atrasadas. {!insights.ai_enabled && <em className="opacity-70">(IA GPT desabilitada - configure OPENAI_API_KEY)</em>}</div>
          )}
        </div>
      )}
    </div>
  );
}
